import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, PERMISSION, can, channelName, conversationApi, disconnectExplanation, formatIst, inboxApi, useAuth, usePaneLayout,
  type Conversation, type ConversationCounts, type InboxCacheEvent, type Message, type Page,
  type TemplateSendExtras, type TenantChannel,
} from '@wa/shared';
import { useActiveConversation, useRealtimeEvents } from '@/notifications/RealtimeProvider';
import { ConversationList } from '@/components/ConversationList';
import { MessageThread } from '@/components/MessageThread';
import { ContactPanel } from '@/components/ContactPanel';
import { InboxFilterPanel } from '@/inbox/InboxFilterPanel';
import { BulkActions } from '@/inbox/BulkActions';
import { NewChatButton, NewChatPanel } from '@/inbox/NewChat';
import { Sheet } from '@/components/Sheet';
import { TemplatePicker } from '@/components/TemplatePicker';
import { ProductPicker } from '@/components/ProductPicker';
import {
  SCOPE_LABEL, STATUSES_FOR, STATUS_LABEL, defaultFilterFor, toQuery,
  type InboxFilter,
} from '@/inbox/filters';

/**
 * The inbox.
 *
 * Layout is decided by how many panes fit, not by guessing from a width:
 *   single — one pane at a time, list → thread → details, each pushing over
 *   dual   — list + thread, details as a right sheet
 *   triple — all three at once
 *
 * There is one component tree for all three. Rendering separate mobile and
 * desktop trees is how the two drift apart and a fix lands on only one of them.
 */
export function InboxPage() {
  const queryClient = useQueryClient();
  const layout = usePaneLayout();
  const { user } = useAuth();
  const seesAll = can(user, PERMISSION.inboxViewAll);

  // The home screen's queue tiles link straight into a group — ?view=sla and the
  // rest. Read once, as the initial state rather than as a synchronised mirror:
  // the filter panel is the owner of this from the first click onwards, and two
  // owners of one value is how a control starts fighting the URL it set.
  //
  // The names in a URL are the panel's four groups plus the two statuses the home
  // screen links at, mapped explicitly rather than cast: a `?view=` is a string
  // from outside, and casting it would let a typo select a group that does not
  // exist and render an empty list with no error anywhere.
  const [params] = useSearchParams();
  // `?open=<conversation id>` lands on that conversation — the Customers screen's
  // "Open chat" arrives this way. The filter widens to "all" for the same reason
  // NewChat widens it: the conversation is otherwise not in the list being shown.
  const openParam = Number(params.get('open')) || null;
  const [filter, setFilter] = useState<InboxFilter>(() => {
    const seed = SEEDS[params.get('view') ?? ''] ?? (openParam ? SEEDS.all : undefined);
    // A seed that names "all" for somebody without inbox.view_all still works —
    // the API narrows it to what they may see — but the panel does not offer that
    // group, so the home screen's links land on My chats for them instead.
    const base = defaultFilterFor(seesAll);
    const seeded = seed ? { ...base, ...seed } : base;
    return seeded.scope === 'all' && !seesAll && !openParam ? { ...seeded, scope: 'mine' } : seeded;
  });
  const [selectedId, setSelectedId] = useState<number | null>(openParam);
  const lastSelectedRow = useRef<Conversation | null>(null);
  const [showViews, setShowViews] = useState(false);
  // Owned here because the grid track is: a panel that shrank itself would
  // leave two hundred pixels of empty column beside it.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  // Owned here rather than in the list, because the actions taken on a selection
  // are: the list draws the ticks, the page knows what an agent may do with them.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // A screen, not a dialog — see NewChatPanel. Held here because it replaces
  // whole columns of this grid rather than floating over them.
  const [newChat, setNewChat] = useState(false);
  // Search inside the open thread. Separate from filter.search, which matches
  // contacts by name and number across the whole list — one box searching two
  // different things is how an agent ends up with an empty screen and no idea why.
  const [threadSearch, setThreadSearch] = useState('');
  // The details column, on a wide screen. Below xl it is a sheet and has its own
  // control, which is why the toggle is only passed at `triple`.
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [pages, setPages] = useState<string[]>([]);

  // Debounced so typing a search does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filter.search), 300);
    return () => clearTimeout(timer);
  }, [filter.search]);

  const query = useMemo(
    () => toQuery({ ...filter, search: debouncedSearch }),
    // Every field toQuery reads must be listed, or changing it leaves the query
    // string — and so the query key, and so the fetch — untouched.
    // Every field toQuery reads is listed. Three were added with the filter panel
    // and forgetting one leaves the query string, the query key and the fetch all
    // untouched — the filter appears to do nothing and reports nothing.
    [filter.scope, filter.status, filter.channelId, filter.tagId, filter.assignedTo,
     filter.from, filter.to, debouncedSearch],
  );
  const queryKey = query.toString();

  // Changing the view resets paging; keeping old pages would mix two filters.
  // It clears the selection too: forty ticks over rows the new filter does not
  // show are forty rows an agent is about to act on without seeing them.
  useEffect(() => { setPages([]); setPicked(new Set()); }, [queryKey]);

  // A status change is fully described by its own event, so it is patched into
  // the cache with no request. New content is not — those refetch, so the live
  // path and a plain reload always render the same thing.
  const onEvent = useCallback((event: InboxCacheEvent) => {
    if (event.type === 'message.status') {
      /*
       * setQueriesData, not setQueryData, and this is the whole bug.
       *
       * The thread is cached under ['messages', id, threadQuery] — three parts,
       * because the in-thread search box is part of what was fetched. This handler
       * wrote to ['messages', id]. React Query matches setQueryData by *exact* key,
       * so every delivery receipt landed in a cache entry nothing was reading, and
       * the ticks on screen never moved until a reload refetched from the database
       * — which had the right status all along.
       *
       * Nothing failed loudly: writing to a key that does not exist is a silent
       * no-op. And the `invalidateQueries` calls elsewhere in this file kept
       * working throughout, because *those* match by prefix — which is exactly why
       * the screen looked alive enough that nobody suspected this.
       *
       * setQueriesData takes a filter and matches by prefix, so it updates the
       * thread whatever is typed in its search box. A delivery receipt is true
       * regardless of what the agent has filtered.
       */
      queryClient.setQueriesData<{ items: Message[]; hasMore: boolean }>(
        { queryKey: ['messages', event.conversationId] },
        (current) => current && {
          ...current,
          items: (current.items ?? []).map(
            (m) => (m.id === event.messageId ? { ...m, status: event.status } : m)),
        },
      );

      /*
       * And the row in the list, which draws its own tick.
       *
       * Patched rather than refetched, for the same reason the thread is: a receipt
       * fully describes itself, and a broadcast to ten thousand contacts would
       * otherwise be ten thousand list refetches to change one glyph each.
       *
       * Matched on `lastMessageId`, not on the conversation. Receipts arrive for
       * whichever message they are about, and the row is about the newest one — so
       * without this test a "delivered" for an older message, landing after a newer
       * message was sent, would pull the row's ticks backwards. The server applies
       * exactly the same guard when it writes the column, so the patch and a reload
       * cannot disagree.
       */
      const tick = (page: Page<Conversation>): Page<Conversation> => ({
        ...page,
        items: (page.items ?? []).map(
          (c) => (c.id === event.conversationId && c.lastMessageId === event.messageId
            ? { ...c, lastMessageStatus: event.status }
            : c)),
      });

      // Two shapes under this prefix, and treating them as one would be a data-loss
      // bug rather than a cosmetic one: the first page is cached as a page object,
      // and every page after it as an *array* of them. An updater that assumed the
      // object shape would spread the array, find no `items`, and replace the
      // agent's loaded pages with an empty list.
      queryClient.setQueriesData<Page<Conversation> | Page<Conversation>[]>(
        { queryKey: ['conversations'] },
        (current) => {
          if (!current) return current;
          return Array.isArray(current) ? current.map(tick) : tick(current);
        },
      );
      return;
    }

    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['counts'] });
    if (event.conversationId === selectedId) {
      void queryClient.invalidateQueries({ queryKey: ['messages', event.conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversation-events', event.conversationId] });

      /*
       * A message that arrives into an open thread has been read.
       *
       * `markRead` was called from `openConversation` and nowhere else, so it fired
       * on *selecting* a conversation and never again. An agent sitting in a chat
       * watching replies come in left every one of them unread: the badge kept
       * counting up next to a conversation they were looking at, and the customer's
       * ticks stayed grey until the agent clicked away and clicked back. Which is
       * exactly what it looked like — "the tick only changes to blue when I re-tap
       * the chat".
       *
       * Gated on the tab being visible, because that is the one claim being made.
       * A conversation left open on a background tab has not been seen by anybody,
       * and telling the customer it has is a lie the product cannot take back.
       */
      if (event.type === 'message.received' && document.visibilityState === 'visible') {
        inboxApi.markRead(event.conversationId)
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            void queryClient.invalidateQueries({ queryKey: ['counts'] });
          })
          .catch(() => { /* a stale badge is not worth interrupting the agent */ });
      }
    }
  }, [queryClient, selectedId]);

  /*
   * What a reconnection has to do, and for three years of this file did not.
   *
   * The hub is the only thing telling this screen that anything happened, and it
   * only speaks while it is connected. SignalR keeps no backlog: every event raised
   * between the socket dropping and the socket coming back is discarded by the
   * server, not queued. So an agent whose laptop slept for twenty seconds, whose
   * wifi changed, whose tab the browser throttled, or who was simply connected while
   * the API was redeployed, came back to a connection in perfect health and an inbox
   * frozen at the moment it dropped — no error, no gap, nothing to notice. The only
   * cure was F5, which is exactly how it was reported.
   *
   * A resumed connection is therefore treated as a fresh page load. Prefix keys, so
   * every cached thread is marked stale and the one on screen refetches.
   */
  const onResumed = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['counts'] });
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-events'] });
  }, [queryClient]);

  /*
   * Subscribes rather than connects, and that is the change.
   *
   * This screen used to call `useInboxHub` itself, which meant the app's only live
   * connection existed exactly while somebody was looking at this screen. It now
   * belongs to the shell, so an agent on Reports still gets a popup and still has a
   * correct tab badge — and two screens wanting events no longer means two
   * websockets, each with its own reconnect loop.
   *
   * The contract is otherwise identical: the same handler, the same resumed
   * callback, the same connection state back.
   */
  const hub = useRealtimeEvents(onEvent, onResumed);
  const live = hub === 'connected';

  // Tells the shell which conversation is on screen, so it stays quiet about this
  // one — a popup about the message you are reading is the fastest way to make
  // somebody switch notifications off.
  useActiveConversation(selectedId);

  /*
   * The backstop, and only while the hub is down.
   *
   * Polling on top of a working connection is duplicate load for the same answer,
   * which is why this file has never done it. Polling *instead of* a connection
   * that is not there is the opposite: it is the difference between an inbox that
   * is a few seconds behind and one that is wrong until somebody reloads it. It
   * stops the moment the socket is back, and a resumed socket refetches anyway.
   */
  const whileOffline = live ? (false as const) : 15_000;

  const first = useQuery({
    queryKey: ['conversations', queryKey],
    queryFn: () => inboxApi.conversations(query),
    refetchInterval: whileOffline,
  });

  const more = useQuery({
    queryKey: ['conversations', queryKey, pages],
    queryFn: async () => {
      const results = [];
      for (const cursor of pages) results.push(await inboxApi.conversations(query, cursor));
      return results;
    },
    enabled: pages.length > 0,
  });

  const conversations = useMemo(() => {
    const seen = new Set<number>();
    const merged: Conversation[] = [];
    for (const item of [...(first.data?.items ?? []), ...(more.data ?? []).flatMap((p) => p.items)]) {
      if (!seen.has(item.id)) { seen.add(item.id); merged.push(item); }
    }
    return merged;
  }, [first.data, more.data]);

  const nextCursor = (more.data?.at(-1) ?? first.data)?.nextCursor ?? null;

  const counts = useQuery({ queryKey: ['counts'], queryFn: inboxApi.counts, refetchInterval: whileOffline });
  const agents = useQuery({ queryKey: ['agents'], queryFn: inboxApi.agents, staleTime: 300_000 });

  // A number that has been unlinked from WhatsApp. Said once at the top of the
  // list and once in place of the composer, with when and why, instead of
  // every send failing one at a time with "channel is no longer active" —
  // which is how Just For You Gifts found out, one message at a time.
  const allChannels = useQuery({ queryKey: ['channels', 'all'], queryFn: inboxApi.allChannels, staleTime: 60_000, refetchInterval: 120_000 });
  const downChannels = useMemo(
    () => (allChannels.data ?? []).filter((c) => !c.isActive && c.disconnectedAt),
    [allChannels.data],
  );
  const downSentence = (c: TenantChannel) =>
    `${channelName(c)} was disconnected from WhatsApp ${formatIst(c.disconnectedAt!)} — ${disconnectExplanation(c)}. Nothing can be sent from it until it is reconnected; contact PSM Softtech.`;

  // Debounced, like the list's. A request per keystroke over a thousand-message
  // thread is a request per keystroke.
  const [debouncedThreadSearch, setDebouncedThreadSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedThreadSearch(threadSearch), 250);
    return () => clearTimeout(timer);
  }, [threadSearch]);

  // Under two characters searches nothing, which is what the server does with it
  // too — asking for one is asking for the whole thread with extra steps.
  const threadQuery = debouncedThreadSearch.trim().length >= 2 ? debouncedThreadSearch.trim() : '';

  const messages = useQuery({
    queryKey: ['messages', selectedId, threadQuery],
    queryFn: () => inboxApi.messages(selectedId!, threadQuery || undefined),
    enabled: selectedId !== null,
    refetchInterval: whileOffline,
  });

  // A second request rather than a field on the first. The messages endpoint pages
  // backwards from the newest and an event from before the current page would be
  // dropped by it; events are counted in tens and fetched whole.
  const events = useQuery({
    queryKey: ['conversation-events', selectedId],
    queryFn: () => conversationApi.events(selectedId!),
    enabled: selectedId !== null,
  });


  // Upload then send: two calls on purpose. The bytes reach Meta while the agent
  // is still looking at the composer, so a large file never sits in the outbox
  // holding up text replies queued behind it.
  const sendMedia = useMutation({
    mutationFn: async ({ id, file, caption, replyToMessageId }: { id: number; file: File; caption: string; replyToMessageId?: number | null }) => {
      const uploaded = await inboxApi.upload(id, file);
      return inboxApi.sendMedia(id, uploaded.mediaAssetId, caption || null, crypto.randomUUID(), replyToMessageId);
    },
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not send the attachment.'),
  });

  // A quick reply's file: uploaded once by an admin, cloned by the server to
  // this chat's number on first use, then sent like any attachment.
  const sendStoredMedia = useMutation({
    mutationFn: async ({ id, channelId, mediaAssetId, caption, replyToMessageId }: { id: number; channelId: number; mediaAssetId: number; caption: string; replyToMessageId?: number | null }) => {
      const usable = await inboxApi.mediaForChannel(mediaAssetId, channelId);
      return inboxApi.sendMedia(id, usable.mediaAssetId, caption || null, crypto.randomUUID(), replyToMessageId);
    },
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not send the attachment.'),
  });

  const send = useMutation({
    mutationFn: ({ id, body, replyToMessageId }: { id: number; body: string; replyToMessageId?: number | null }) =>
      inboxApi.send(id, body, crypto.randomUUID(), undefined, replyToMessageId),
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not send the message.'),
  });

  const sendTemplate = useMutation({
    mutationFn: ({ id, templateId, variables, extras }: { id: number; templateId: number; variables: string[]; extras?: TemplateSendExtras }) =>
      inboxApi.sendTemplate(id, templateId, variables, crypto.randomUUID(), extras),
    onSuccess: () => {
      setSendError(null);
      setShowTemplates(false);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    // Left open on failure: the reason is almost always something the agent can
    // fix in the form they are already looking at.
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not send the template.'),
  });

  const sendProducts = useMutation({
    mutationFn: (body: { conversationId: number; body: string; header: string | null; footer: string | null; retailerIds: string[]; wholeCatalogue: boolean }) =>
      inboxApi.sendProducts({ ...body, idempotencyKey: crypto.randomUUID() }),
    onSuccess: () => {
      setSendError(null);
      setShowProducts(false);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not send the products.'),
  });

  const addNote = useMutation({
    mutationFn: ({ id, body, mentionedAgentIds }: { id: number; body: string; mentionedAgentIds: number[] }) =>
      inboxApi.addNote(id, body, mentionedAgentIds),
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not save the note.'),
  });

  const deleteNote = useMutation({
    mutationFn: inboxApi.deleteNote,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
    },
  });

  /**
   * Reacting.
   *
   * Applied to the cache before the request so the chip appears on the tap, and
   * rolled back on failure. This is the one place optimism is worth the code: the
   * endpoint calls Graph inline rather than queueing, so an honest spinner would
   * be a visible pause on every thumbs-up.
   */
  const scheduled = useQuery({
    queryKey: ['scheduled', selectedId],
    queryFn: () => inboxApi.scheduled(selectedId ?? undefined),
    enabled: selectedId !== null,
  });

  const schedule = useMutation({
    mutationFn: ({ id, body, when }: { id: number; body: string; when: string }) =>
      inboxApi.send(id, body, crypto.randomUUID(), when),
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['scheduled', selectedId] });
    },
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not schedule that message.'),
  });

  const cancelScheduled = useMutation({
    mutationFn: inboxApi.cancelScheduled,
    onSuccess: () => {
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: ['scheduled', selectedId] });
      void queryClient.invalidateQueries({ queryKey: ['messages', selectedId] });
    },
    // 409 when the drain claimed it a moment ago. The message is worth showing
    // verbatim: "already on its way" is the honest answer, not a generic failure.
    onError: (error) =>
      setSendError(error instanceof ApiError ? error.message : 'Could not cancel that message.'),
  });

  const react = useMutation({
    mutationFn: ({ id, messageId, emoji }: { id: number; messageId: number; emoji: string | null }) =>
      emoji === null
        ? inboxApi.clearReaction(id, messageId)
        : inboxApi.setReaction(id, messageId, emoji),

    onMutate: async ({ id, messageId, emoji }) => {
      await queryClient.cancelQueries({ queryKey: ['messages', id] });

      // Every cached variant of this thread, not one: getQueryData needs the exact
      // three-part key and would return undefined here, making the rollback below
      // silently restore nothing.
      const previous = queryClient.getQueriesData({ queryKey: ['messages', id] });

      queryClient.setQueriesData<{ items: Message[]; notes: unknown[]; hasMore: boolean }>(
        { queryKey: ['messages', id] },
        (old) => {
          if (!old || user === null) return old;

          return {
            ...old,
            items: old.items.map((m) => {
              if (m.id !== messageId) return m;

              // Mine is replaced, not appended — one reaction per person is the
              // rule the table enforces, and the UI must not briefly show two.
              const others = (m.reactions ?? []).filter(
                (r) => !(r.reactorKind === 1 && r.agentId === user.id),
              );

              return {
                ...m,
                reactions: emoji === null ? others : [
                  ...others,
                  {
                    emoji,
                    reactorKind: 1,
                    agentId: user.id,
                    agentName: user.displayName,
                    reactedAt: new Date().toISOString(),
                  },
                ],
              };
            }),
          };
        },
      );

      return { previous, id };
    },

    onError: (error, _variables, context) => {
      // Restore each variant to exactly what it held before the optimistic write.
      if (context) {
        for (const [key, data] of context.previous) queryClient.setQueryData(key, data);
      }
      setSendError(error instanceof ApiError ? error.message : 'Could not send the reaction.');
    },

    // Reconcile with the server either way: the optimistic row carries no id and
    // a concurrent reaction from the customer would otherwise be lost.
    onSettled: (_data, _error, variables) =>
      queryClient.invalidateQueries({ queryKey: ['messages', variables.id] }),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof inboxApi.update>[1] }) =>
      inboxApi.update(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
    },
  });

  // The open thread must survive its row leaving the list. In the Unread view
  // the row is gone the moment it is marked read; in Awaiting reply, the moment
  // the agent replies; in Unassigned, the moment they take it — and each of those
  // was replacing the thread with "Select a conversation" half a second after it
  // opened. The last row seen for the selected id stands in until the list has
  // it again (or the agent opens something else).
  const fromList = conversations.find((c) => c.id === selectedId);
  if (fromList) lastSelectedRow.current = fromList;
  const remembered = lastSelectedRow.current?.id === selectedId ? lastSelectedRow.current : undefined;

  // Arrived by `?open=` at a conversation the list does not hold. The list is
  // the newest thirty by last activity; a broadcast report links to a reply from
  // three weeks ago, and the Customers screen to a chat that may never have been
  // on page one. Nothing was seen for it, so nothing is remembered for it, and
  // the pane said "Select a conversation" with the id sitting right there in the
  // URL. Fetched alone, in the list's own shape, under the list's own
  // visibility rules; the row joins the list on its own the next time it moves.
  const standalone = useQuery({
    queryKey: ['conversation', selectedId],
    queryFn: () => inboxApi.conversation(selectedId!),
    enabled: selectedId !== null && !fromList && !remembered,
    staleTime: 60_000,
  });
  if (standalone.data && standalone.data.id === selectedId) lastSelectedRow.current = standalone.data;

  const selected: Conversation | undefined =
    fromList ?? remembered ?? (standalone.data?.id === selectedId ? standalone.data : undefined);

  // Nothing is auto-selected on any layout. The desktop used to open the first
  // row on arrival "so the pane is not empty", and that first row is the newest
  // conversation — the one most likely to receive the next message, which the
  // open-thread rule above then marked read and ticked blue for a customer nobody
  // had read. Landing on the list is what every sign-in and every click on Inbox
  // asked for; a thread opens when somebody opens it (or arrives by ?open=).

  function openConversation(id: number) {
    setSelectedId(id);
    setSendError(null);
    setShowViews(false);

    // Reading the thread is reading the mention. Fired unconditionally because
    // the list does not carry whether this conversation holds one, and the call
    // is a no-op when it does not.
    inboxApi.markMentionsRead(id)
      .then(() => queryClient.invalidateQueries({ queryKey: ['counts'] }))
      .catch(() => { /* a stale badge is not worth interrupting the agent */ });

    const conversation = conversations.find((c) => c.id === id);
    if (conversation && conversation.unreadCount > 0) {
      // Clears our badge and turns the customer's ticks blue. It is not the only
      // caller any more: a message arriving into an already-open thread marks
      // itself read in `onEvent`, because opening a conversation is not the only
      // moment an agent reads one.
      inboxApi.markRead(id)
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ['conversations'] });
          void queryClient.invalidateQueries({ queryKey: ['counts'] });
        })
        .catch(() => { /* a stale badge is not worth interrupting the agent */ });
    }
  }

  if (first.isError) {
    return (
      <div className="mx-auto max-w-md px-6 py-16 text-center">
        <h2 className="text-lead font-semibold">Could not load conversations</h2>
        <p className="mt-2 text-body text-(--color-muted)">
          {first.error instanceof ApiError ? first.error.message : 'The API did not respond.'}
        </p>
      </div>
    );
  }

  // "All chats" on its own, "My chats · Unread" when a status narrows it. The
  // group alone when the status is that group's default, because "All chats ·
  // Open" above a list of open chats is a heading that repeats the control that
  // produced it.
  const viewLabel = filter.status === STATUSES_FOR[filter.scope][0]
    ? SCOPE_LABEL[filter.scope]
    : `${SCOPE_LABEL[filter.scope]} · ${STATUS_LABEL[filter.status]}`;

  const list = filter.surface === 'groups' ? (
    <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 border-(--color-line) bg-(--color-surface) px-6 text-center md:border-r">
      <span aria-hidden="true" className="grid size-12 place-items-center rounded-full bg-(--color-sunken) text-(--color-muted)">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <circle cx="9" cy="9.5" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M3.6 18.4a5.6 5.6 0 0 1 10.8 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M16 7.2a2.7 2.7 0 0 1 0 5M20.4 18.4a5.4 5.4 0 0 0-1.4-3.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <p className="text-body font-medium">Groups are not switched on</p>
      {/* Named, not vague. "Coming soon" tells an agent nothing and tells whoever
          would have to chase Meta even less. */}
      <p className="max-w-xs text-small text-(--color-muted)">
        WhatsApp group messaging needs an Official Business Account — the green tick —
        which is a Meta application rather than something we build. It also caps a
        group at eight people and supports neither read receipts nor buttons.
      </p>
      <button
        type="button"
        onClick={() => setFilter((f) => ({ ...f, surface: 'chats' }))}
        className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small font-medium hover:bg-(--color-sunken)"
      >
        Back to chats
      </button>
    </div>
  ) : (
    <div className="relative flex h-full min-h-0 flex-col border-(--color-line) bg-(--color-surface) md:border-r">
      {/*
        A frozen inbox that looks perfectly normal is the part that turned this into
        a support call rather than a refresh. The hook has always known the answer;
        nothing ever asked it. Nothing is shown while the connection is healthy —
        a permanent "connected" badge is decoration that teaches people to ignore
        the one state that matters.
      */}
      {downChannels.map((c) => (
        <div key={c.id} role="alert" className="flex items-start gap-2 border-b border-(--color-warn) bg-(--color-warn)/8 px-4 py-2 text-small text-(--color-ink-soft)">
          <span aria-hidden="true" className="mt-1.5 size-2 shrink-0 rounded-full bg-(--color-warn)" />
          <span>{downSentence(c)}</span>
        </div>
      ))}
      {!live && (
        <div
          role="status"
          className="flex items-center gap-2 border-b border-(--color-line) bg-(--color-sunken) px-4 py-2 text-small text-(--color-muted)"
        >
          <span aria-hidden="true" className="size-2 shrink-0 animate-pulse rounded-full bg-(--color-warn)" />
          <span>
            {hub === 'connecting' ? 'Connecting to live updates…' : 'Reconnecting — new messages may be a few seconds late.'}
          </span>
        </div>
      )}
      <ConversationList
      conversations={conversations}
      selectedId={selectedId}
      onSelect={openConversation}
      isLoading={first.isPending}
      search={filter.search}
      onSearch={(search) => setFilter((f) => ({ ...f, search }))}
      onOpenViews={layout === 'triple' ? undefined : () => setShowViews(true)}
      onLoadMore={() => nextCursor && setPages((p) => [...p, nextCursor])}
      hasMore={nextCursor !== null}
      isLoadingMore={more.isFetching}
      title={viewLabel}
      total={totalFor(filter, counts.data)}
      picked={picked}
      onPickedChange={setPicked}
      actions={<BulkActions ids={[...picked]} onDone={() => setPicked(new Set())} />}
    />

      {/* Inside the list's own column and absolutely placed, so it stays put while
          the list scrolls and never covers the thread. Hidden while a selection is
          running: a floating "New" over a bulk action bar is two jobs at once. */}
      {picked.size === 0 && <NewChatButton onNewChat={() => setNewChat(true)} />}
    </div>
  );

  const thread = selected ? (
    <MessageThread
      // A new instance per conversation: the composer's draft, a chosen attachment,
      // a half-written note and "internal note" mode all belong to one chat, and
      // carrying them into the next one sent customer A's file to customer B.
      key={selected.id}
      conversation={selected}
      messages={messages.data?.items ?? []}
      notes={messages.data?.notes ?? []}
      // No system pills while searching. They are context, not content: an
      // "assigned to Asha" pill sitting among two search results reads as a third
      // result, and it matches nothing the agent typed.
      events={threadQuery ? [] : (events.data ?? [])}
      agents={agents.data ?? []}
      isLoading={messages.isPending}
      isSending={send.isPending}
      sendError={sendError}
      onSend={(body, replyToMessageId) => send.mutateAsync({ id: selected.id, body, replyToMessageId })}
      onSendMedia={(file, caption, replyToMessageId) => sendMedia.mutateAsync({ id: selected.id, file, caption, replyToMessageId })}
      onSendStoredMedia={(mediaAssetId, caption, replyToMessageId) =>
        sendStoredMedia.mutateAsync({ id: selected.id, channelId: selected.channelId, mediaAssetId, caption, replyToMessageId })}
      isUploading={sendMedia.isPending || sendStoredMedia.isPending}
      onOpenTemplates={() => { setSendError(null); setShowTemplates(true); }}
      onOpenProducts={() => { setSendError(null); setShowProducts(true); }}
      onAddNote={(body, mentionedAgentIds) =>
        addNote.mutate({ id: selected.id, body, mentionedAgentIds })}
      onDeleteNote={(id) => deleteNote.mutate(id)}
      isSavingNote={addNote.isPending}
      myAgentId={user?.id ?? null}
      onReact={(messageId, emoji) => react.mutate({ id: selected.id, messageId, emoji })}
      isReacting={react.isPending}
      scheduled={scheduled.data ?? []}
      onSchedule={(body, when) => schedule.mutate({ id: selected.id, body, when })}
      onCancelScheduled={(id) => cancelScheduled.mutate(id)}
      onBack={layout === 'single' ? () => setSelectedId(null) : undefined}
      onOpenDetails={layout === 'triple' ? undefined : () => setShowDetails(true)}
      onResolve={() => update.mutate({ id: selected.id, patch: { status: 2 } })}
      onReopen={() => update.mutate({ id: selected.id, patch: { status: 0 } })}
      channelDown={(() => { const c = downChannels.find((x) => x.id === selected.channelId); return c ? downSentence(c) : null; })()}
      onStatus={(status) => update.mutate({ id: selected.id, patch: { status } })}
      onSendTemplate={(templateId, variables) =>
        sendTemplate.mutate({ id: selected.id, templateId, variables })}
      onAssign={(agentId) =>
        update.mutate({ id: selected.id, patch: agentId === null ? { unassign: true } : { assignedAgentId: agentId } })}
      search={threadSearch}
      onSearch={setThreadSearch}
      onToggleDetails={layout === 'triple' ? () => setDetailsOpen((v) => !v) : undefined}
      detailsOpen={layout === 'triple' ? detailsOpen : showDetails}
      isSaving={update.isPending}
    />
  ) : (
    <div className="chat-ground grid h-full place-items-center px-8 text-center">
      <p className="max-w-xs text-body text-(--color-muted)">
        {selectedId !== null && standalone.isPending
          ? 'Opening the conversation…'
          : selectedId !== null && standalone.data === null
            ? 'That conversation is not in your inbox, or you do not have access to it.'
            : 'Select a conversation to read it.'}
      </p>
    </div>
  );

  const details = selected && (
    <ContactPanel
      conversation={selected}
      agents={agents.data ?? []}
      notes={messages.data?.notes ?? []}
      onAddNote={(body, mentionedAgentIds) =>
        addNote.mutate({ id: selected.id, body, mentionedAgentIds })}
      isSavingNote={addNote.isPending}
      scheduled={scheduled.data ?? []}
      onViewScheduled={() => setShowDetails(false)}
      onClose={layout === 'triple' ? undefined : () => setShowDetails(false)}
    />
  );

  const newChatPanel = (
    <NewChatPanel
      onClose={() => setNewChat(false)}
      onStarted={(id) => {
        setNewChat(false);
        // Widen the filter before selecting it.
        //
        // Landing on "Select a conversation to read it" is what happens otherwise,
        // and it looks like the action failed: a chat started with somebody whose
        // conversation is resolved — or on another channel, or assigned elsewhere —
        // is simply not in the list the current filter is showing. You asked for
        // that conversation, so the list moves to where it can be seen.
        setFilter((f) => ({ ...f, scope: 'all', status: 'all', search: '' }));
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['counts'] });
        openConversation(id);
      }}
    />
  );

  const filters = (
    <InboxFilterPanel
      counts={counts.data}
      filter={filter}
      onChange={(next) => { setFilter((f) => ({ ...f, ...next })); setShowViews(false); }}
      onClose={layout === 'triple' ? undefined : () => setShowViews(false)}
      collapsed={filtersCollapsed}
      onCollapsedChange={setFiltersCollapsed}
    />
  );

  const productPicker = selected && (
    <ProductPicker
      open={showProducts}
      onClose={() => setShowProducts(false)}
      isSending={sendProducts.isPending}
      sendError={sendError}
      onSend={(body) => sendProducts.mutate({ conversationId: selected.id, ...body })}
    />
  );

  const templatePicker = selected && (
    <TemplatePicker
      open={showTemplates}
      conversationId={selected.id}
      onClose={() => setShowTemplates(false)}
      isSending={sendTemplate.isPending}
      sendError={sendError}
      onSend={(templateId, variables, extras) =>
        sendTemplate.mutate({ id: selected.id, templateId, variables, extras })}
    />
  );


  if (layout === 'single') {
    return (
      <div className="h-full min-h-0">
      {/* The page's own name, for a screen reader and for anything counting
          headings. The visible ones are h2 and belong to panes: the list's title
          changes with the view, the filter panel's is in a sheet below lg, and on
          a phone with a conversation open neither is on screen — so none of them
          can be the h1. Every other screen in this portal has one; this screen
          had none from M1 until it was looked for.
          */}
      <h1 className="sr-only">Inbox</h1>
        {newChat ? newChatPanel : selected ? thread : list}
        <Sheet open={showViews} side="left" label="Filters" onClose={() => setShowViews(false)}>{filters}</Sheet>
        <Sheet open={showDetails && Boolean(selected)} side="bottom" label="Contact details" onClose={() => setShowDetails(false)}>
          {details}
        </Sheet>
        {templatePicker}
        {productPicker}
      </div>
    );
  }

  if (layout === 'dual') {
    return (
      <div className="grid h-full min-h-0 grid-cols-[320px_minmax(0,1fr)]">
      {/* The page's own name, for a screen reader and for anything counting
          headings. The visible ones are h2 and belong to panes: the list's title
          changes with the view, the filter panel's is in a sheet below lg, and on
          a phone with a conversation open neither is on screen — so none of them
          can be the h1. Every other screen in this portal has one; this screen
          had none from M1 until it was looked for.
          */}
      <h1 className="sr-only">Inbox</h1>
        {newChat ? <div className="col-span-2 min-h-0">{newChatPanel}</div> : <>{list}{thread}</>}
        <Sheet open={showViews} side="left" label="Filters" onClose={() => setShowViews(false)}>{filters}</Sheet>
        <Sheet open={showDetails && Boolean(selected)} side="right" label="Contact details" onClose={() => setShowDetails(false)}>
          {details}
        </Sheet>
        {templatePicker}
        {productPicker}
      </div>
    );
  }

  return (
    // Four columns here, five with the icon rail outside this component: filters,
    // list, thread, details. The filter column came back — it was a row of chips
    // while there were eight views and no other filters, and fifteen controls in a
    // chip row wraps to four lines and moves the list every time it changes.
    //
    // 220 + 300 for the two narrow columns rather than one 360: the same 520px, and
    // the list is a list rather than a list with a filter panel bolted to its head.
    // 244px for the filter column, not 220: at 220 "Awaiting reply" and "All Chats"
    // both ran into an ellipsis, and a filter whose own name is cut off is a filter
    // somebody has to hover to read. Measured off the screenshots being matched,
    // which give it ~238. Collapsed it is 44px, and the thread takes the rest.
    //
    // Two whole class strings rather than an interpolated one: Tailwind reads
    // these at build time and a template literal produces a class it never
    // generated, which fails as a silently unstyled grid.
    //
    // grid-template-columns interpolates between two track lists of the same
    // shape, so the column is animated directly rather than by animating a width
    // inside it — which would leave the three columns beside it snapping to their
    // new positions while only the first one slid.
    <div className={[
      'grid h-full min-h-0 transition-[grid-template-columns] duration-200 ease-out',
      'motion-reduce:transition-none',
      // Four templates, all written out. Tailwind reads these at build time and a
      // template literal produces a class it never generated, which fails as a
      // silently unstyled grid.
      //
      // The new-chat form is 420px — its own column, the width the design being
      // matched gives it — with the chat ground beside it, rather than a form
      // stretched across three columns where "Start Chat" would be a metre wide.
      //
      // The reference's own layout constants, read off its :root — list 360px,
      // detail drawer 360px — from 2xl up. At xl (1280) those two would leave the
      // thread 260px wide, so the narrower tracks stay until there is room.
      newChat
        ? (filtersCollapsed
            ? 'grid-cols-[44px_420px_minmax(0,1fr)]'
            : 'grid-cols-[244px_420px_minmax(0,1fr)]')
        : detailsOpen
          ? (filtersCollapsed
              ? 'grid-cols-[44px_300px_minmax(0,1fr)_290px] 2xl:grid-cols-[44px_360px_minmax(0,1fr)_360px]'
              : 'grid-cols-[244px_300px_minmax(0,1fr)_290px] 2xl:grid-cols-[244px_360px_minmax(0,1fr)_360px]')
          // Closed, the details column is a zero-width track rather than a removed
          // one, so the grid animates between the two instead of the thread jumping.
          : (filtersCollapsed
              ? 'grid-cols-[44px_300px_minmax(0,1fr)_0px] 2xl:grid-cols-[44px_360px_minmax(0,1fr)_0px]'
              : 'grid-cols-[244px_300px_minmax(0,1fr)_0px] 2xl:grid-cols-[244px_360px_minmax(0,1fr)_0px]'),
    ].join(' ')}>
      {/* The page's own name, for a screen reader and for anything counting
          headings. The visible ones are h2 and belong to panes: the list's title
          changes with the view, the filter panel's is in a sheet below lg, and on
          a phone with a conversation open neither is on screen — so none of them
          can be the h1. Every other screen in this portal has one; this screen
          had none from M1 until it was looked for.
          */}
      <h1 className="sr-only">Inbox</h1>
      {/* overflow-hidden, or the expanded panel's 244px of content spills across
          the conversation list for the 200ms the column takes to shrink. */}
      <div className="min-h-0 overflow-hidden border-r border-(--color-line)">{filters}</div>

      {/* The form replaces the list, and the thread and details columns become one
          empty ground beside it — which is what the design being matched shows and
          is the right shape: entering a number is the whole task while it is
          happening, and a form floating over a chat list invites the reader to
          keep reading the chat list. */}
      {newChat ? (
        <>
          {newChatPanel}
          <div className="chat-ground min-h-0" />
        </>
      ) : (
        <>
          {list}
          {thread}
          {/* overflow-hidden, or a 290px panel spills across the thread for the
              200ms the column takes to close. */}
          <div className="min-h-0 overflow-hidden">
            {selected ? details : <div className="h-full border-l border-(--color-line) bg-(--color-surface)" />}
          </div>
        </>
      )}
      {templatePicker}
      {productPicker}
    </div>
  );
}

/**
 * The badge beside the list's title.
 *
 * The view keys and the count keys used to be the same set, so this was an index.
 * They are not any more — the panel has Open and Closed where the counts have
 * `open` and `closed`, and SLA breached where they have `slaBreached` — and an
 * index into a type that no longer covers the key is exactly the sort of thing
 * TypeScript catches and a plain object lookup does not.
 */
function totalFor(filter: InboxFilter, counts: ConversationCounts | undefined): number | undefined {
  // One lookup into the same grouped figures the panel's badges read, so the
  // heading and the badge beside it can never disagree — which they did while
  // they came from two different sets of keys.
  return counts?.scopes?.[filter.scope]?.[filter.status];
}

/**
 * What a `?view=` in a URL is allowed to mean.
 *
 * A closed set, not a cast. The home screen links four of these; anything else
 * falls through to the default rather than selecting a group that does not exist.
 */
const SEEDS: Record<string, Partial<InboxFilter>> = {
  open: { scope: 'all', status: 'open' },
  closed: { scope: 'all', status: 'closed' },
  unread: { scope: 'all', status: 'unread' },
  awaiting: { scope: 'all', status: 'awaiting' },
  all: { scope: 'all', status: 'all' },
  mine: { scope: 'mine', status: 'all' },
  unassigned: { scope: 'unassigned', status: 'all' },
  sla: { scope: 'sla', status: 'all' },
};
